import { v4 } from 'uuid'
import * as _ from 'lodash'
import { EMPTY_RESPONSE_MESSAGE } from 'Src/constants'

export const getStepByInternalName = (items, searchKey, value) => {
  return Object.values(items).find((i) => i[searchKey] === value) ?? {}
}

export const getEscalateStep = (steps) => {
  return getStepByInternalName(steps, 'internal_name', 'escalate')
}

export const getEndConversationStep = (steps) => {
  return getStepByInternalName(steps, 'internal_name', 'end-conversation')
}

const getAcknowledgementMessages = (data) => {
  const defaultItem = { message: 'Do any of these answers match what you are looking for?' }
  const msgList = data?.acknowledgement_messages ? _.cloneDeep(data.acknowledgement_messages) : []
  if (!msgList || !msgList.length) {
    return [defaultItem]
  }

  return msgList.map((msg) => {
    return { message: msg }
  })
}

const getFeedbackMessages = (data) => {
  const defaultItem = { message: 'Do you need more help?' }
  const msgList = data?.feedback_messages ? _.cloneDeep(data.feedback_messages) : []
  if (!msgList || !msgList.length) {
    return [defaultItem]
  }

  return msgList.map((msg) => {
    return { message: msg }
  })
}

export const cleanKBSections = (selectedKBs, allKBs) => {
  const findCommonSections = (arr1, arr2) => {
    return arr1.filter((item) => arr2.includes(item))
  }

  return selectedKBs.flatMap((selectedKB) => {
    const matchingKB = allKBs.find((kb) => kb.kb_id === selectedKB.kb_id)
    return matchingKB
      ? [
          {
            kb_id: selectedKB.kb_id,
            sections: findCommonSections(selectedKB.sections, matchingKB.sections)
          }
        ]
      : []
  })
}

export const getDefaultBotSettings = (data, escalateStep, stepId, allKBs, accountData) => {
  const notHelpfulActionStep = escalateStep?.step_id ?? undefined
  const noResultActionStep = stepId ?? undefined

  let values = data.selected_sections ? [...data.selected_sections] : []
  if (allKBs) {
    values = [...cleanKBSections([...values], allKBs)]
  }
  const selectedSections = convertToSectionObject([...values])

  const showEmptyResponseMessage = accountData.settings?.ai_predictions
  const emptyResponseMessage =
    data?.empty_response_message || EMPTY_RESPONSE_MESSAGE.replace('{companyName}', accountData?.company_name)

  return {
    question_placeholder: data?.question_placeholder ?? 'Type your question here...',
    acknowledgementMessages: getAcknowledgementMessages(data) ?? [],
    feedbackMessages: getFeedbackMessages(data) ?? [],
    helpfulLabel: data?.helpful_label ?? 'No, I am all set',
    helpfulAction: data?.helpful_action ?? {
      type: 'endChat',
      followupMessage: 'Glad that helped. Thank you for reaching out to us. Have a great day!'
    },
    notHelpfulLabel: data?.not_helpful_label ?? 'Contact support',
    notHelpfulAction: data?.not_helpful_action ?? { type: 'goToStep', step: notHelpfulActionStep },
    noResultLabel: data?.no_result_label ?? 'Need more help',
    noResultAction: data?.no_result_action ?? { type: 'goToStep', step: noResultActionStep },
    enableKBFilter: data?.enable_kb_filter || false,
    enableTagsFilter: data?.enable_tags_filter || false,
    selectedSections,
    selectedTags: data?.selected_tags || [],
    ...(showEmptyResponseMessage && {
      emptyResponseMessage
    })
  }
}

export const convertToSectionObject = (kbSections) => {
  if (kbSections) {
    return kbSections.reduce((obj, item) => {
      obj[item.kb_id.toString()] = item.sections
      return obj
    }, {})
  }
  return {}
}

export const convertToSectionArray = (kbObject) => {
  if (kbObject)
    return Object.entries(kbObject)
      .map(([kb_id, sections]) => {
        return { kb_id: parseInt(kb_id), sections }
      })
      .filter((item) => item.sections?.length)

  return []
}

export const getMlActionPayload = (data, escalateStep, stepId, actionId = undefined, accountData) => {
  let settings = data
  if (!actionId) {
    settings = getDefaultBotSettings(data, escalateStep, stepId, undefined, accountData) ?? {}
  }

  const cleanedSections = convertToSectionArray(settings.selectedSections)
  const showEmptyResponseMessage = accountData.settings?.ai_predictions

  return {
    stepId,
    actionId: actionId ?? v4(),
    setting: {
      type: 'ml_search',
      collect_feedback: true,
      acknowledgement_messages: settings.acknowledgementMessages.map((ack) => ack.message || ack),
      feedback_messages: settings.feedbackMessages.map((feed) => feed.message || feed),
      helpful_label: settings.helpfulLabel,
      helpful_action: { ...settings.helpfulAction },
      not_helpful_label: settings.notHelpfulLabel,
      not_helpful_action: { ...settings.notHelpfulAction },
      no_result_label: settings.noResultLabel,
      no_result_action: { ...settings.noResultAction },
      question_placeholder: settings.question_placeholder,
      selected_sections: [...cleanedSections],
      selected_tags: [...settings.selectedTags],
      enable_kb_filter: settings.enableKBFilter,
      enable_tags_filter: settings.enableTagsFilter,
      ...(showEmptyResponseMessage && {
        empty_response_message: settings.emptyResponseMessage
      })
    }
  }
}
